/**
 * Sidebar meter elements (`#voice-meter`, `#voice-meter-bar`,
 * `#voice-ptt-hint`) driven by `VoiceConnection`'s level and
 * speaking-state callbacks. Owns the `PushToTalk` input controller
 * that translates keyboard PTT into `VoiceConnection.startSpeaking/
 * stopSpeaking` calls.
 *
 * Mode-aware visuals — the meter colour and PTT hint text change
 * based on which modifier is held when PTT engages:
 *
 *   - say     (V)        : default accent
 *   - shout   (Shift+V)  : warm orange + bold caps
 *   - whisper (Ctrl+V)   : cool purple + italics
 */

import { PushToTalk } from "@/audio/PushToTalk";
import type { VoiceConnection, VoiceMode } from "@/audio/VoiceConnection";

const MODE_LABELS: Record<VoiceMode, string> = {
  say: "🎙 Speaking…",
  shout: "📣 SHOUTING…",
  whisper: "🤫 Whispering…",
};

const MODE_HINT = "Hold V · Shift=shout · Ctrl=whisper";

export class VoiceMeter {
  private meter = document.getElementById("voice-meter") as HTMLElement | null;
  private bar = document.getElementById("voice-meter-bar") as HTMLElement | null;
  private hint = document.getElementById("voice-ptt-hint") as HTMLElement | null;
  private ptt: PushToTalk | null = null;

  init(): void {
    if (!this.meter || !this.bar || !this.hint) return;
    this.hint.textContent = MODE_HINT;

    this.ptt = new PushToTalk({ pttKey: "v" });
    this.ptt.attach();
  }

  bindControl(control: VoiceConnection): void {
    this.ptt?.setControl(control);
  }

  /**
   * VoiceConnection's level callback — wired in via ZoneScene as the
   * `onLevel` option. Updates the meter bar width.
   */
  handleLevel = (rms: number): void => {
    if (!this.bar) return;
    this.bar.style.width = `${Math.round(rms * 100)}%`;
  };

  /**
   * VoiceConnection's speaking-change callback — wired in via ZoneScene
   * as the `onSpeakingChange` option. Toggles meter visibility and
   * updates the mode label / hint.
   */
  handleSpeakingChange = (speaking: boolean, mode: VoiceMode): void => {
    this.applyModeClass(mode);
    this.meter?.toggleAttribute("hidden", !speaking);
    this.hint?.classList.toggle("active", speaking);
    if (this.hint) {
      this.hint.textContent = speaking ? MODE_LABELS[mode] : MODE_HINT;
    }
  };

  destroy(): void {
    this.ptt?.destroy();
    this.ptt = null;
  }

  private applyModeClass(mode: VoiceMode): void {
    if (!this.meter) return;
    this.meter.classList.remove("mode-say", "mode-shout", "mode-whisper");
    this.meter.classList.add(`mode-${mode}`);
  }
}
